/**
 * Training SOP — Administrator track.
 *
 * Written as institutional memory rather than instruction: today this is one
 * person's job, and the point of writing it down is that it survives them
 * being unavailable. So it favours "why it is this way" and "what has bitten
 * us" over button locations, and states the consequences of the destructive
 * levers plainly.
 *
 * Note the Admin PANEL is gated on the `Admin` ROLE, not on the `admin.access`
 * permission — a user holding the permission without the role passes every API
 * check but has no route to the page. The one exception is the Feedback Admin
 * role (`feedback.manage`): it opens the panel, but the page shows that role the
 * Feedback sub-tab alone, so none of this track applies to it.
 *
 * Verified against the code (2026-07-25 sweep) and the incidents of that day.
 */
import { OWNERSHIP, type Track } from '../types';

export const ADMIN_TRACK: Track = {
  id: 'admin',
  label: 'Administrator',
  audience:
    'Whoever holds the Admin role. Covers access control, company settings, the Sage connection, and the lists that keep the rest of the app working.',
  suggestedFor: { adminOnly: true },
  // Admin-only as a whole, not just topic by topic. There is exactly one Admin.
  access: { adminOnly: true },
  sections: [
    // ── 1. People and access ────────────────────────────────────────────────
    {
      id: 'admin-access',
      title: 'People and access',
      intro:
        'Access is two separate things: which ROLES someone holds, and which DIVISIONS they can see across. Both live on the same screen and are edited the same way.',
      topics: [
        {
          id: 'admin-users',
          title: 'Adding someone and setting their access',
          summary:
            'Most people appear automatically the first time they sign in. You can also add someone ahead of time using their Entra object ID, which is useful when you want their access ready on day one.',
          steps: [
            'Go to **Admin ▸ Users & Roles**.',
            'Find the person, or add them by Entra OID if they have never signed in.',
            'Check the roles they need, and any divisions they should see beyond their own assignments.',
            'Save.',
          ],
          watchOut: [
            '**Roles and divisions save as a full replacement, not an addition.** Unchecking removes the grant — there is no separate remove step, and no confirmation.',
            'Permissions are re-read on every request, so a change takes effect on the person\'s **next page load**. Nobody needs to sign out and back in.',
          ],
          access: { adminOnly: true },
        },
        {
          id: 'admin-roles',
          title: 'Roles and permissions',
          summary:
            'A role is just a named bundle of permissions. Built-in roles can be edited but not deleted; roles you create yourself can be deleted when unused.',
          steps: [
            'Go to **Admin ▸ Roles** to see every role and the full permission catalog.',
            'Use **+ Add Role** for a new bundle, or open an existing role to change what it grants.',
          ],
          watchOut: [
            '**The Admin role cannot be renamed or deleted, deliberately.** The permission system recognizes administrators by that exact name — renaming it would lock every administrator out of the system, including you. The app refuses the change rather than letting it happen.',
            'Admin bypasses permission checks entirely. Checking or unchecking boxes on the Admin role changes nothing in practice; they are shown for visibility only.',
            'Granting a permission to a role affects everyone holding it. Check the user count before widening something.',
          ],
          access: { adminOnly: true },
        },
        {
          id: 'admin-field-leaders',
          title: 'How field leaders get their access',
          summary:
            'Field leaders are different from office staff: their access comes from being assigned to a job, not from a role you grant by hand. Assignment is what lets them see the job and write its daily logs.',
          watchOut: [
            'Assigning someone as a field leader gives them **Project Tracker access only**. They also need the job\'s SharePoint folder shared with them separately, or their photo uploads will fail. This is the most common field-leader complaint.',
            'A field-leader-only account sees a single Projects tab and no cost figures anywhere. That is by design, not a misconfiguration.',
          ],
          access: { adminOnly: true },
        },
      ],
    },

    // ── 2. Troubleshooting someone's access ─────────────────────────────────
    {
      id: 'admin-view-as',
      title: 'Troubleshooting someone\'s access',
      topics: [
        {
          id: 'admin-impersonate',
          title: 'Viewing the app as another user',
          summary:
            'Rather than guessing why someone cannot see something, look at the app through their account. It is strictly read-only and has no effect on them at all.',
          steps: [
            'Go to **Admin ▸ Users & Roles** and use **View as** on their row.',
            'The app reloads as them, with a banner across the top reminding you.',
            'Use **Exit "View as"** when you are done.',
          ],
          watchOut: [
            '**Everything is read-only while viewing as someone.** Any attempt to save is refused, so you cannot accidentally change their data or act as them.',
            'It does not touch their session. They can be signed in and working the whole time and will notice nothing.',
            '⚠️ One blind spot: roles that come from an Entra group rather than from this app are **not** reflected. A field leader whose role arrives that way may appear to have no role at all in this view. Do not conclude their access is broken on that basis alone.',
          ],
          access: { adminOnly: true },
        },
      ],
    },

    // ── 3. Company settings ─────────────────────────────────────────────────
    {
      id: 'admin-config',
      title: 'Company settings',
      intro:
        'A short list of switches with company-wide reach. Two of them cause most of the confused questions.',
      topics: [
        {
          id: 'admin-show-costs',
          title: 'The cost visibility switch',
          summary:
            '**Show Costs on Project Detail** is a master switch. Turned off, the Financials and Commitments tabs disappear and every dollar figure on Materials goes with them — for everyone at once, including administrators.',
          watchOut: [
            'If several people report that money "vanished" at the same moment, check this first. It is almost always the answer.',
            'It overrides permissions. Granting someone financial access while this is off achieves nothing.',
          ],
          access: { adminOnly: true },
        },
        {
          id: 'admin-docs-parent',
          title: 'The job folder parent — a yearly job',
          summary:
            'One-click "Create Job Folder" needs to know where new job folders go. That location is stored in a setting called **docsJobsParentUrl**, and it points at the current financial year\'s folder.',
          steps: [
            'Once the new FY folder exists in SharePoint, go to **Admin ▸ Config**.',
            'Find the key **docsJobsParentUrl** and update it to the new folder\'s URL.',
          ],
          watchOut: [
            '⚠️ **This needs doing once a year** and nothing reminds you. Until it is updated, new jobs get folders created under last year — or the button tells PMs to "ask an admin" and they will.',
            'The key appears under its raw name with no friendly label, so searching Config for "Documents" or "Job Folder" will not find it. Look for **docsJobsParentUrl** exactly.',
          ],
          access: { adminOnly: true },
        },
      ],
    },

    // ── 4. The Sage connection ──────────────────────────────────────────────
    {
      id: 'admin-integrations',
      title: 'The Sage Intacct connection',
      topics: [
        {
          id: 'admin-sync',
          title: 'Running and reading a sync',
          summary:
            'Sage data — purchase orders, contract values, change orders — is pulled in overnight at 3 AM. Admin ▸ Integrations is the only place a sync can be started by hand, which is why people ask you.',
          owner: OWNERSHIP.sageSync,
          steps: [
            'Go to **Admin ▸ Integrations**.',
            'Use **Sync now · all jobs**.',
            'Watch the status panel for the result — when it last ran, who ran it, how many lines matched, and how many need review.',
          ],
          watchOut: [
            'The button returns **immediately**, before the sync has finished. It means "started", not "done" — a full pull takes minutes. Read the status panel rather than the button.',
            'The connection is **one-way and read-only**. The Project Tracker never writes anything back to Intacct, so a sync can never damage your accounting data.',
            'Only history from 2024 onward is pulled. Older job numbers will never match.',
          ],
          access: { adminOnly: true },
        },
        {
          id: 'admin-review-list',
          title: 'What "needs review" actually means',
          summary:
            'The review list is narrower than it sounds. It shows only purchase orders that more than one material line is claiming — a genuine ambiguity the system refuses to guess at.',
          watchOut: [
            'Plain untracked PO lines are **normal and expected** — they include sales tax, freight, equipment rental and everything else the field does not track. They are not a backlog and do not appear in the review list.',
            'A PO attaches to a material line automatically only when the job and cost code identify exactly one line. Anything ambiguous is surfaced rather than guessed — that is the behavior working, not failing.',
          ],
          access: { adminOnly: true },
        },
      ],
    },

    // ── 5. Lists you maintain ───────────────────────────────────────────────
    {
      id: 'admin-lists',
      title: 'Lists you maintain',
      topics: [
        {
          id: 'admin-owned-equipment',
          title: 'The owned equipment catalog',
          summary:
            'This list is what fills the Owned Equipment # picker for the operations team. If a unit is missing from their dropdown, it is missing from here.',
          steps: [
            'Go to **Admin ▸ Owned Equipment**.',
            'Add the unit number, a description and a class.',
          ],
          watchOut: [
            '**Retire, do not delete.** Retiring takes a unit out of the picker while leaving it recorded on every job it was already assigned to. Deleting is only for something entered by mistake.',
          ],
          access: { adminOnly: true },
        },
        {
          id: 'admin-feedback',
          title: 'The feedback queue',
          summary:
            'Everything submitted through the in-app Feedback button lands here, with the submitter, their role, the page they were on, and any screenshots. Each one also opens a GitHub issue automatically, and a card is posted to Teams. The item and its issue are kept in step: closing or reopening either one does the same to the other.',
          steps: [
            'Go to **Admin ▸ Feedback** — it opens filtered to **Open**.',
            'Read the item, download any attachments, follow the GitHub link if you need the technical thread.',
            'Press **💬 Reply** under an item to answer everyone following it: the person who sent it, and anyone who said “me too”. They see the reply under **My feedback**, and everyone can read it if the item is on the board. It is copied to the GitHub issue as a comment. The panel says how many people follow the item.',
            'Use **Done** when it is dealt with, or **Won’t do** when it will not be. The GitHub issue closes too, the people following the item see which, and an item closed on GitHub closes here by itself. To say why, open **💬 Reply** and write in **Close with a note** first: the note is what they are told, in the same message as the close.',
            'If an item looks out of step with GitHub, press **Sync with GitHub**. It checks every item now instead of waiting for the nightly check, and says what it changed.',
            'The **Board** column says whether other people can read an item. Feedback sent before the board opened to everyone, and anything sent from a browser tab that was already open then, starts **Unreviewed**, which only the admins and the person who sent it can read. So does all new feedback: **new feedback is not public by default**, and goes on the board only when an admin shows it. (For a short time after the board opened, feedback went on it as soon as it was sent, unless its sender ticked **Keep this private**; those items stay on it.) The exception is feedback whose sender ticked **Keep this private**: that reads **Private**, it is the sender’s choice, and nobody can change it, an admin included. Press **To review** to list the items nobody has looked at, tick the ones that should be shared (**Select all unreviewed** ticks up to 100 at a time, and never an item you hid earlier: tick that one on its own), and press **Show on board**. A preview lists each message and every reply on it, in full, with the names of the people who wrote the replies, before anything is shown. Untick any item that should not be shared, then confirm.',
            'Press **Hide…** on an item that is on the board to take it off. The item is not deleted, and it can be shown again. The people who already follow it keep it under **My feedback** and keep being emailed, unless you tick **Also stop telling them**. That removes them, it cannot be undone, and if it cannot be done the item is not hidden either. You can also do it later: a hidden item that people other than its sender still follow shows **Stop telling followers…** in the Board column, and it does the same thing (everyone but the sender is removed and not told, it leaves their **My feedback**, showing the item again does not put them back), for that item only. The person who sent it is never removed. On an item nobody has reviewed, **Not for the board…** marks it as reviewed and not for the board, which takes it off the **To review** list without ever showing it.',
            'Press **Merge…** under an item’s status when it asks for the same thing as another request that is already on the board. A dialog suggests the open requests on the board that read like it, and you can search the board for any it missed. Only a request that is **on the board and still open** can be chosen, because everyone merged into it will read it. Read the preview before you confirm: the item is **closed as a duplicate** and leaves the board, everyone who follows it and the person who sent it are **added to the other request** (anyone already following it is left as they are, and nobody is removed from this one, so they keep it under **My feedback** as **Combined**), the person who sent it gets **one email** (unless that is you) and nobody else is emailed, including anyone who said “me too”, and on GitHub its issue gets a comment saying it duplicates the other request and is **closed as not planned**. **A merge cannot be undone from here.** A private item cannot be merged, and neither can one closed as done or as won’t do until it is reopened; one closed as a duplicate, on GitHub for example, can be. A merged item reads **Combined into …** in the Status column, has no **Reopen**, and is never shown on the board again. The result says what happened on GitHub: a comment that could not be posted is not retried, so add it yourself if you want it on the issue, and a close that has not landed shows **Not on GitHub yet** until the nightly check or **Sync with GitHub** sends it. The email to the person who sent it is sent in the background, and the screen is not told whether it arrived.',
          ],
          watchOut: [
            'A reply goes to everyone following the item: the person who sent it, and anyone who said “me too” on the board. They are emailed it as well; the person who wrote the reply is not. It is saved before it is copied to GitHub, so if GitHub is down you are told the reply was saved but not copied. If replies could not be loaded for the list, the panel says so and does not offer a reply box.',
            'Showing an item makes every reply on it public with it, including replies written while only the sender could read them, and each reply shows its author’s name. The sender’s name is never shown. That is why the preview lists every reply, and why an item whose replies could not be loaded cannot be shown. The reply box says when what you write will be public.',
            'A dash in the Issue column means the GitHub side failed — the feedback itself is still safely recorded here. The database is the source of truth; GitHub and Teams are conveniences on top.',
            'A ⚠ under an issue number means the two sides disagree, and **⚠ Out of step** beside **Open** and **All** lists every such item. It only appears when there is one, and **Sync with GitHub** takes you straight to it when it finds any. **Not on GitHub yet** means a change you made has not reached GitHub; it is retried every night, and **Sync with GitHub** retries it now. **Still open on GitHub** on a closed item means it was closed here before the two were linked, so nothing told GitHub. Press **Close it on GitHub** if the work is done, or **Reopen** if it is not. Nothing is closed or reopened for you in that case, because only a person can say which side is right.',
          ],
          access: { adminOnly: true },
        },
      ],
    },

    // ── 6. Hard-won lessons ─────────────────────────────────────────────────
    {
      id: 'admin-lessons',
      title: 'Things that have bitten us',
      intro: 'Short, and worth reading before making changes at the database level.',
      topics: [
        {
          id: 'admin-id-collision',
          title: 'Never identify a role or permission by its ID number',
          summary:
            'In July 2026 a database change matched roles and permissions by ID rather than by name. Two IDs were already taken, so instead of adding new entries it **renamed the existing ones**. The Receiving Editor role vanished, and for about three weeks only administrators could edit receiving orders — with nothing to indicate anything was wrong.',
          watchOut: [
            'Always match on the **name** (a role\'s label, a permission\'s code), never on an ID number. The IDs are internal and are not consistent between environments — the test and live databases genuinely disagree on which number belongs to which permission.',
            'The lesson generalizes: a silent access change produces no error, no alert and no complaint until someone happens to need the thing. Verify access changes by checking who holds what afterwards, not by the absence of errors.',
          ],
        },
        {
          id: 'admin-gate-drift',
          title: 'Screens and rules can drift apart',
          summary:
            'Several places once showed buttons to people who were not allowed to use them, so saving failed with a permission error. Users reasonably read that as the feature being broken.',
          watchOut: [
            'If someone reports "the button does nothing" or "it says I am not allowed", that is worth passing on rather than dismissing — it usually means a screen and its rule have gone out of step, which is a fixable bug rather than a training problem.',
          ],
        },
      ],
    },
  ],
};
